import Box, { BoxProps } from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import AttachmentGroupButton from "csi/attachment/AttachmentGroupButton";
import { TYPE_COMMENT_REPLY } from "csi/model/comment";
import { CommentDetail } from "csi/state/comments";
import { useStore } from "csi/state/hooks";
import { cnl } from "csi/typings";
import UserAvatar from "csi/user/UserAvatar";
import UserGroupButton from "csi/user/UserGroupButton";
import FlexRowCenter from "designSystem/components/flex/FlexRowCenter";
import GridItemGrow from "designSystem/components/grid/GridItemGrow";
import OverflowText from "designSystem/components/text/OverflowText";
import Text from "designSystem/components/text/Text";
import { useLocale } from "designSystem/locale";
import { grey } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent } from "react";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";
import CommentAuthorAndDate from "./CommentAuthorAndDate";
import CommentStatusIcon from "./CommentStatusIcon";
import CommentsRevisionChip from "./CommentsRevisionChip";

const PREFIX = "CommentSummaryCard";

const classes = {
	grid: `${PREFIX}grid`,
	category: `${PREFIX}category`,
	content: `${PREFIX}content`,
	replies: `${PREFIX}replies`,
};

const StyledBox = styled(Box)(({ theme }) => ({
	[`& .${classes.grid}`]: {
		cursor: "pointer",

		"& *": {
			cursor: "pointer",
		},
	},

	[`& .${classes.category}`]: {
		backgroundColor: grey[200],
		borderColor: grey[400],
		maxWidth: "100%",
		height: 20,
	},

	[`& .${classes.content}`]: {
		"& *": {
			backgroundColor: "unset !important",
			fontSize: `${MINSIZE}px !important`,
			lineHeight: "16px !important",
		},
		"& span.fr-emoticon.fr-deletable.fr-emoticon-img": {
			// handle the emojis
			minHeight: MINSIZE,
			height: MINSIZE,
		},
		"& table *": {
			// handle the table
			lineHeight: "14px !important",
		},
	},

	[`& .${classes.replies}`]: {
		whiteSpace: "nowrap",
	},
}));

const MINSIZE = 12;

interface CommentSummaryCardProps extends BoxProps {
	/** comment url */
	commentUrl: cnl;
}

const CommentSummaryCard: FunctionComponent<CommentSummaryCardProps> = observer((props) => {
	const loc = useLocale();
	const { commentsStore } = useStore();
	const { finishInitialization, hoveredCommentUrl } = commentsStore;
	const { commentUrl, ...cardProps } = props;

	const commentSummary = commentsStore.getCommentSummary(commentUrl);
	const {
		category: commentSummaryCategory,
		hasAttachments: commentSummaryHasAttachments,
		isResolved: commentSummaryIsResolved,
		lastReplyUrl: commentSummaryLastReplyUrl,
		numberOfReplies: commentSummaryNumberOfReplies,
		participants: commentSummaryParticipants,
		pinName: commentSummaryPinName,
		relatedHref: commentSummaryRelatedHref,
		relatedName: commentSummaryRelatedName,
		relatedType: commentSummaryRelatedType,
		subject: commentSummarySubject,
		threeDInfo: commentSummaryThreeDInfo,
	} = commentSummary;
	const commentDetail = commentsStore.getCommentDetail(commentUrl) || {} as CommentDetail;
	const {
		author: commentDetailAuthor,
		content: commentDetailContent,
		revisionCount: commentDetailRevisionCount,
	} = commentDetail;
	
	const replyDetail = commentSummaryLastReplyUrl ? commentsStore.getCommentDetail(commentSummaryLastReplyUrl) : null;
	const {
		author: replyDetailAuthor,
		content: replyDetailContent,
		revisionCount: replyDetailRevisionCount,
	} = replyDetail || {};

	function handleMouseEnter() {
		commentsStore.setHoveredCommentByUrl(commentUrl);
	};

	function handleMouseLeave() {
		commentsStore.setHoveredCommentByUrl(null);
	};

	return (
		<>
			{finishInitialization && (
				<StyledBox
					{...cardProps}
					bgcolor={hoveredCommentUrl === commentUrl ? grey[200] : "inherit"}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
					<Grid
						className={classes.grid}
						container
						spacing={1}
						wrap="nowrap"
					>
						<Grid item>
							<Box pl={3} py={3}>
								<UserAvatar user={commentDetailAuthor} />
							</Box>
						</Grid>
						<GridItemGrow>
							<Box pr={3} py={3}>
								<FlexRowCenter height={24}>
									<CommentAuthorAndDate commentDetail={commentDetail} />
									<CommentStatusIcon design="inactive" isResolved={commentSummaryIsResolved} />
								</FlexRowCenter>
								<Box mt={2}>
									<OverflowText
										bold
										size={14}
										text={commentSummarySubject}
									/>
								</Box>
								{commentSummaryRelatedType && (
									<Box mt={2}>
										<Text
											block
											bold
											text={`${commentSummaryRelatedType}:`}
										/>
										<Link href={commentSummaryRelatedHref}>
											<OverflowText semiBold text={commentSummaryRelatedName} />
										</Link>
									</Box>
								)}
								{commentDetailContent && (
									<Box
										className={classes.content}
										height={32}
										mt={3}
										overflow="hidden"
									>
										<FroalaEditorView model={commentDetailContent} />
									</Box>
								)}
								<CommentsRevisionChip
									pinName={commentSummaryPinName}
									revisionCount={commentDetailRevisionCount}
									threeDInfo={commentSummaryThreeDInfo}
								/>
								<FlexRowCenter height={26} mt={3}>
									<Box
										flexGrow={1}
										mr={2}
										overflow="hidden"
									>
										{commentSummaryCategory && (
											<Chip
												className={classes.category}
												label={(
													<OverflowText
														lineHeight={12}
														semiBold
														size={10}
														text={commentSummaryCategory}
													/>
												)}
												size="small"
												variant="outlined"
											/>
										)}
									</Box>
									<Text
										className={classes.replies}
										color="textPrimary"
										text={loc.numberOfType(commentSummaryNumberOfReplies, TYPE_COMMENT_REPLY)}
									/>
									<Box
										bgcolor={grey[600]}
										height={12}
										ml={2}
										mr={1}
										width="1px"
									/>
									<UserGroupButton
										displayAsLink
										owner={commentDetailAuthor}
										users={commentSummaryParticipants}
									/>
									{commentSummaryHasAttachments && (
										<Fragment>
											<Box
												bgcolor={grey[600]}
												height={12}
												mx={1}
												width="1px"
											/>
											<AttachmentGroupButton commentUrl={commentUrl} />
										</Fragment>
									)}
								</FlexRowCenter>
								{replyDetail && (
									<Box
										borderTop={STANDARD_BORDER_STYLE}
										mt={3}
										pt={3}
									>
										<Text
											block
											bold
											lineHeight={14}
											text={`${loc.resource("LatestReply")}:`}
										/>
										<FlexRowCenter mt={2}>
											<Box mr={2}>
												<UserAvatar user={replyDetailAuthor} />
											</Box>
											<CommentAuthorAndDate commentDetail={replyDetail} />
										</FlexRowCenter>
										<Box
											className={classes.content}
											height={16}
											mt={2}
											overflow="hidden"
										>
											<FroalaEditorView model={replyDetailContent} />
										</Box>
										<CommentsRevisionChip revisionCount={replyDetailRevisionCount} />
									</Box>
								)}
							</Box>
						</GridItemGrow>
					</Grid>
				</StyledBox>
			)}
		</>
	);
});

export default CommentSummaryCard;
